import { useState } from 'react';
import { useParams, Link } from 'wouter';
import { SEOMeta } from '../components/common/SEOMeta';
import { blogPosts } from '../constant/BlogPosts';
import { ROUTES } from '../routes/Routes';
import '../assets/css/pages/BlogDetail.css';

export default function BlogDetail() {
  const params = useParams<{ slug: string }>();
  const post = blogPosts.find(p => p.slug === params.slug);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  if (!post) {
    return (
      <div className="blog-detail-not-found">
        <i className="fa-solid fa-triangle-exclamation"></i>
        <h2>Blog not found</h2>
        <Link href={ROUTES.BLOG.path} className="btn-solid">Back to Blog</Link>
      </div>
    );
  }

  const categoryColors: Record<string, string> = {
    'Web Dev': 'linear-gradient(135deg, #0f2027, #0f3460)',
    'Mobile': 'linear-gradient(135deg, #0f2027, #2c5364)',
    'Cloud': 'linear-gradient(135deg, #0a2342, #1e3a5f)',
    'Design': 'linear-gradient(135deg, #1f0533, #5a1091)',
    'AI/ML': 'linear-gradient(135deg, #0a3d0a, #1a5c1a)',
    'Security': 'linear-gradient(135deg, #1a0a0a, #7b2d2d)',
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(prev => (prev === index ? null : index));
  };

  return (
    <>
      <SEOMeta
        title={post.seo.metaTitle}
        description={post.seo.metaDescription}
      />

      <div className="bd-hero" style={{ background: categoryColors[post.category] || 'linear-gradient(135deg, #161b22, #243b55)' }}>
        <div className="bd-hero-icon"><i className={`fa-solid fa-${post.icon}`}></i></div>
        <div className="bd-hero-content container">
          <Link href={ROUTES.BLOG.path} className="bd-back"><i className="fa-solid fa-arrow-left"></i> Back to Blog</Link>
          <span className="blog-cat-badge">{post.category}</span>
          <h1 className="bd-title">{post.title}</h1>
          <div className="bd-meta">
            <span><i className="fa-regular fa-calendar"></i> {post.date}</span>
            <span><i className="fa-regular fa-clock"></i> {post.readTime}</span>
          </div>
        </div>
      </div>

      <div className="bd-body container">
        <div className="bd-content">
          <p className="bd-lead">{post.excerpt}</p>
          <h2>Introduction</h2>
          <p>{post.introduction}</p>
          <h2>Key Concepts</h2>
          <p>{post.keyConceptsDescription}</p>
          <ul>
            {post.keyConcepts.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
          <h2>Best Practices</h2>
          <p>{post.bestPracticesDescription}</p>
           <ul>
            {post.bestPractices.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
          <blockquote><p>"{post.bestPracticesBlockquote}"</p></blockquote>
          <h2>Conclusion</h2>
          <p>{post.conclusion}</p>

          {post.faqs && post.faqs.length > 0 && (
            <div className="bd-faq">
              <div className="bd-faq-header">
                <span className="bd-faq-label"><i className="fa-solid fa-circle-question"></i> FAQ</span>
                <h2 className="bd-faq-title">Frequently Asked Questions</h2>
                <p className="bd-faq-subtitle">Quick answers to common questions about this topic.</p>
              </div>
              <div className="bd-faq-list">
                {post.faqs.map((faq, i) => (
                  <div
                    key={i}
                    className={`bd-faq-item${openFaq === i ? ' open' : ''}`}
                  >
                    <button
                      className="bd-faq-question"
                      onClick={() => toggleFaq(i)}
                      aria-expanded={openFaq === i}
                    >
                      <span className="bd-faq-q-num">0{i + 1}</span>
                      <span className="bd-faq-q-text">{faq.question}</span>
                      <span className="bd-faq-chevron">
                        <i className="fa-solid fa-chevron-down"></i>
                      </span>
                    </button>
                    <div className="bd-faq-answer">
                      <div className="bd-faq-answer-inner">
                        <p>{faq.answer}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bd-cta-box">
            <h3>Ready to build something extraordinary?</h3>
            <p>Let's discuss your requirements and create a tailored solution.</p>
            <Link href={ROUTES.CONTACT.path} className="btn-solid">Get in Touch</Link>
          </div>
        </div>

        <aside className="bd-sidebar">
          <div className="bd-sidebar-card">
            <h4>Other Articles</h4>
            <ul className="bd-related">
              {blogPosts.filter(p => p.slug !== post.slug).slice(0, 4).map(p => (
                <li key={p.slug}>
                  <Link href={`/blog/${p.slug}`} className="bd-related-link">
                    <i className={`fa-solid fa-${p.icon}`}></i>
                    <span>{p.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="bd-sidebar-card bd-contact-card">
            <i className="fa-solid fa-phone-volume"></i>
            <h4>Have a Project?</h4>
            <p>Talk to our team today.</p>
            <Link href={ROUTES.CONTACT.path} className="bd-contact-btn">Contact Us</Link>
          </div>
        </aside>
      </div>
    </>
  );
}